import { useState } from 'react';
import { CreateFarmPriceInput } from '@/types/database/farm-prices';
import * as XLSX from 'xlsx';

interface ParseProgress {
  current: number;
  total: number;
  stage: 'reading' | 'parsing' | 'complete';
}

export const useFarmPriceExcelParser = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string>('');
  const [progress, setProgress] = useState<ParseProgress>({ current: 0, total: 0, stage: 'reading' });

  // Helper function to find column value with various name variations
  const findColumnValue = (row: any, possibleNames: string[]): any => {
    // First, get all the keys from the row and normalize them
    const rowKeys = Object.keys(row);
    
    for (const possibleName of possibleNames) {
      // Try exact match first
      if (row[possibleName] !== undefined && row[possibleName] !== null && row[possibleName] !== '') {
        return row[possibleName];
      }
      
      // Try case-insensitive match with trimmed spaces
      const normalizedName = possibleName.toLowerCase().trim();
      for (const key of rowKeys) {
        const normalizedKey = key.toLowerCase().trim();
        if (normalizedKey === normalizedName) {
          const value = row[key];
          if (value !== undefined && value !== null && value !== '') {
            return value;
          }
        }
      }
    }
    
    return null;
  };

  // Helper function to parse Excel dates
  const parseExcelDate = (dateValue: any): string => {
    if (!dateValue) return new Date().toISOString().split('T')[0];
    
    try {
      // If it's already a valid date string
      if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
        return dateValue;
      }
      
      // If it's an Excel serial number
      if (typeof dateValue === 'number') {
        // Excel date serial number conversion
        const excelEpoch = new Date(1899, 11, 30);
        const days = dateValue > 60 ? dateValue - 1 : dateValue; // Excel leap year bug
        const jsDate = new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
        return jsDate.toISOString().split('T')[0];
      }
      
      // Try to parse as regular date
      const parsed = new Date(dateValue);
      if (!isNaN(parsed.getTime())) {
        return parsed.toISOString().split('T')[0];
      }
      
      return new Date().toISOString().split('T')[0];
    } catch (err) {
      console.error('Date parsing error:', err, dateValue);
      return new Date().toISOString().split('T')[0];
    }
  };

  const parseExcelFile = async (file: File): Promise<CreateFarmPriceInput[]> => {
    setError('');
    setIsProcessing(true);
    setProgress({ current: 0, total: 0, stage: 'reading' });

    try {
      // Stage 1: Reading file
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      console.log('Excel data preview:', jsonData.slice(0, 2));
      console.log('Available columns:', jsonData.length > 0 ? Object.keys(jsonData[0]) : []);

      // Stage 2: Parsing data with progress tracking
      setProgress({ current: 0, total: jsonData.length, stage: 'parsing' });
      
      const parsedPrices: CreateFarmPriceInput[] = [];
      const batchSize = 50; // Process in batches to allow UI updates

      for (let i = 0; i < jsonData.length; i += batchSize) {
        const batch = jsonData.slice(i, i + batchSize);
        
        for (let j = 0; j < batch.length; j++) {
          const row = batch[j];
          const rowIndex = i + j;
          
          try {
            // Find required fields with multiple possible column names
            const productName = findColumnValue(row, [
              'Product List', 'Product Name', 'product_list', 'product_name', 'Product'
            ]);
            
            const region = findColumnValue(row, [
              'Region', 'region'
            ]);
            
            const timestamp = findColumnValue(row, [
              'Timestamp', 'Date', 'timestamp', 'date'
            ]);

            // Validate required fields
            if (!productName) {
              throw new Error(`Row ${rowIndex + 2}: Product List/Product Name is required but not found. Available columns: ${Object.keys(row).join(', ')}`);
            }
            
            if (!region) {
              throw new Error(`Row ${rowIndex + 2}: Region is required but not found. Available columns: ${Object.keys(row).join(', ')}`);
            }
            
            if (!timestamp) {
              throw new Error(`Row ${rowIndex + 2}: Timestamp/Date is required but not found. Available columns: ${Object.keys(row).join(', ')}`);
            }

            // Parse optional fields with fallback to 0
            const farmPrice = Number(findColumnValue(row, [
              'Farm Price Per Kg', 'farm_price_per_kg', 'Farm Price'
            ])) || 0;
            
            const sackCratePrice = Number(findColumnValue(row, [
              'Sack_or_crate_cost_per_kg', 'Sack or crate cost per kg', 'sack_crate_cost_per_kg', 'Sack Cost'
            ])) || 0;
            
            const loadingPrice = Number(findColumnValue(row, [
              'Loading cost per Kg', 'loading_cost_per_kg', 'Loading Cost'
            ])) || 0;
            
            const transportPrice = Number(findColumnValue(row, [
              'Transportation_cost_per_Kg', 'Transportation cost per Kg', 'transportation_cost_per_kg', 'Transport Cost'
            ])) || 0;
            
            const borderPrice = Number(findColumnValue(row, [
              'Border cost per kg', 'border_cost_per_kg', 'Border Cost'
            ])) || 0;
            
            const agentPrice = Number(findColumnValue(row, [
              'Agent cost per Kg', 'agent_cost_per_kg', 'Agent Cost'
            ])) || 0;
            
            const miscPrice = Number(findColumnValue(row, [
              'Miscellaneous Costs', 'miscellaneous_costs', 'Misc Costs'
            ])) || 0;

            const vat = Number(findColumnValue(row, [
              'VAT', 'vat'
            ])) || 0;

            // Calculate total price (excluding VAT)
            const totalPrice = farmPrice + sackCratePrice + loadingPrice + transportPrice + borderPrice + agentPrice + miscPrice;

            parsedPrices.push({
              product_name: String(productName).trim(),
              region: String(region).trim(),
              origin: findColumnValue(row, [
                'Product Origin', 'product_origin', 'Origin'
              ]) ? String(findColumnValue(row, [
                'Product Origin', 'product_origin', 'Origin'
              ])).trim() : undefined,
              source_type: findColumnValue(row, [
                'Farm Source Type', 'source_type', 'Source Type'
              ]) ? String(findColumnValue(row, [
                'Farm Source Type', 'source_type', 'Source Type'
              ])).trim() : undefined,
              supplier_name: findColumnValue(row, [
                'Supplier Name', 'supplier_name', 'Supplier'
              ]) ? String(findColumnValue(row, [
                'Supplier Name', 'supplier_name', 'Supplier'
              ])).trim() : undefined,
              farm_price_per_kg: farmPrice,
              sack_crate_cost_per_kg: sackCratePrice,
              loading_cost_per_kg: loadingPrice,
              transportation_cost_per_kg: transportPrice,
              border_cost_per_kg: borderPrice,
              agent_cost_per_kg: agentPrice,
              miscellaneous_costs: miscPrice,
              vat: vat,
              price: totalPrice,
              product_remark: findColumnValue(row, [
                'Product Remark', 'product_remark', 'Remark', 'Notes'
              ]) ? String(findColumnValue(row, [
                'Product Remark', 'product_remark', 'Remark', 'Notes'
              ])).trim() : undefined,
              date: parseExcelDate(timestamp),
            });
          } catch (rowError) {
            console.error(`Error processing row ${rowIndex + 2}:`, rowError);
            throw rowError;
          }
        }

        // Update progress
        setProgress({ 
          current: Math.min(i + batchSize, jsonData.length), 
          total: jsonData.length, 
          stage: 'parsing' 
        });

        // Allow UI to update
        await new Promise(resolve => setTimeout(resolve, 10));
      }

      setProgress({ current: jsonData.length, total: jsonData.length, stage: 'complete' });
      console.log('Parsed prices preview:', parsedPrices.slice(0, 2));
      return parsedPrices;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse Excel file';
      console.error('Excel parsing error:', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    parseExcelFile,
    isProcessing,
    error,
    progress,
    setError
  };
};
